<script lang="ts">
	/**
	 * Slider component for displaying media and custom elements
	 */
	import Swiper from 'swiper';
	import { Navigation, Pagination, Autoplay } from 'swiper/modules';
	import 'swiper/css';

	import 'swiper/css/navigation';
	import 'swiper/css/pagination';
	import { onMount } from 'svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import type { SwiperOptions } from 'swiper/types/swiper-options';

	type SliderType = 'single' | 'multi';

	/**
	 * Props
	 */
	export let type: SliderType = 'single'; // (Optional) Type of the slider (singe = only one slide visible, multi = 3 slides visible), can be overwritten with the options prop
	export let name: string = ''; // Name of the slider (required for distinguishing between multiple sliders on page)

	export let slidesPerView: number | null | 'auto' = null; // (Optional) prop for overwriting slidesPerView
	export let slidesPerViewPhone: number | null | 'auto' = null; // (Optional) prop for overwriting slidesPerView
	export let slidesPerViewTablet: number | null | 'auto' = null; // (Optional) prop for overwriting slidesPerView
	export let slidesPerViewDesktopSmall: number | null | 'auto' = null; // (Optional) prop for overwriting slidesPerView
	export let slidesPerViewDesktop: number | null | 'auto' = null; // (Optional) prop for overwriting slidesPerView
	export let navigation: boolean = true; // (Optional) If navigation arrows should be shown
	export let centeredSlides: boolean = true; // (Optional) If navigation arrows should be shown
	export let pagination: boolean = true; // (Optional) If pagination points should be shown
	export let options: any = {}; // Additional options for the swiper (Is the same like the default swiper options object)
	export let arrowType: 'arrow' | 'slider-arrow' = 'slider-arrow';

	export let swiper: Swiper | undefined = undefined;
	// let resizeTimeout: any;

	onMount(() => {
		const opt: SwiperOptions = {
			modules: [Navigation, Pagination, Autoplay],
			loop: true,
			grabCursor: true,
			centeredSlides: centeredSlides,
			slidesPerView: slidesPerView ?? 1,
			spaceBetween: type === 'single' ? 0 : 16,

			breakpoints: {
				500: {
					slidesPerView: slidesPerViewPhone || (type == 'single' ? 1 : 1.5)
				},
				800: {
					slidesPerView: slidesPerViewTablet || (type == 'single' ? 1 : 2)
				},
				1000: {
					slidesPerView: slidesPerViewDesktopSmall || (type == 'single' ? 1 : 4)
				},
				1200: {
					slidesPerView: slidesPerViewDesktop || (type == 'single' ? 1 : 5)
				}
			},

			pagination: pagination
				? {
						el: '.swiper-pagination.' + name
				  }
				: false,

			...options
		};

		swiper = new Swiper('.swiper.' + name, opt);
	});
</script>

{#if navigation}
	<div class="navigation">
		<button
			class="slider-nav prev"
			on:click={() => {
				swiper?.slidePrev(400);
			}}
		>
			<GlobalMedia key={arrowType === 'slider-arrow' ? 'slider-prev' : 'icon-arrow-left'} />
		</button>
		<button
			class="slider-nav next"
			on:click={() => {
				swiper?.slideNext(400);
			}}
		>
			<GlobalMedia key={arrowType === 'slider-arrow' ? 'slider-next' : 'icon-arrow-right'} />
		</button>
	</div>
{/if}
<div class="swiper {name}">
	<div class="swiper-wrapper">
		<!-- {#if swiper} -->
		<!-- this check stop the slides from getting huge until the slider is initalized -->
		<slot />
		<!-- {/if} -->
	</div>
	<div class="swiper-pagination {name}" />
</div>

<style lang="scss">
	.swiper {
		width: 100%;
		max-height: 100%;
	}

	.navigation {
		padding-bottom: v(content-gap);
		display: flex;
		width: 100%;
		justify-content: flex-end;
		gap: v(content-gap-xs);
	}

	.slider-nav {
		border: none;
		background: transparent;
		transition: all 0.3s ease;
		width: 16px;
		height: 16px;

		padding: 0;

		&:hover {
			filter: brightness(0.2);
		}
	}

	:global(.swiper-pagination) {
		display: flex;
		gap: v(content-gap-sm);
		justify-content: center;
		height: 20px;

		@include media(phone) {
			gap: v(content-gap-2xs);
		}
	}
	:global(.swiper-pagination-bullet) {
		background-color: color(white);
	}

	:global(.swiper-pagination-bullet-active) {
		background-color: color(white);
		opacity: 1;
	}
</style>
